import { useEffect, useMemo, useState } from 'react';
import converter from 'bech32-converting';
import type { EthersError } from 'ethers';
import { useSnackbar } from 'notistack';
import { useDebounce } from 'use-debounce';
import { Interface } from '@ethersproject/abi';
import { Typography } from '@mui/material';

import NumberTypo from 'src/components/common/Number';
import Tooltip from 'src/components/common/Tooltip';
import GradientButton from 'src/components/GradientButton';
import NetworkBadge from 'src/components/NetworkBadge';
import TransactionBottomSheet from 'src/components/TransactionBottomSheet';
import { ERC20_ABI, WETH_DYDX_ABI } from 'src/constants/abi';
import { RPC_ERROR_MESSAGE } from 'src/constants/error';
import { MAINNET } from 'src/constants/network';
import { DYDX_TOKEN, DYDX_TOKEN_ADDRESS, WETH_DYDX_TOKEN_ADDRESS } from 'src/constants/tokens';
import useEstimatedFeeSWR from 'src/hooks/SWR/ethereum/useEstimatedFeeSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { useAllowanceSWR } from 'src/hooks/useAllowanceSWR';
import { useBalanceSWR } from 'src/hooks/useBalanceSWR';
import { useCoinGeckoPriceSWR } from 'src/hooks/useCoinGeckoPriceSWR';
import { useCurrentTab } from 'src/hooks/useCurrentTab';
import { useCurrentWallet } from 'src/hooks/useCurrentWallet';
import useProvider from 'src/hooks/useProivder';
import { useTokenBalanceSWR } from 'src/hooks/useTokenBalanceSWR';
import { useTxInfoSWR } from 'src/hooks/useTxInfoSWR';
import type { RPCError } from 'src/types/error';
import { bridgingClickEvent, pageView } from 'src/utils/ga';
import { fix, gt, isDecimal, times, toBaseDenomAmount, toDisplayDenomAmount, trimZeros } from 'src/utils/number';

import TokenBalanceInfo from './components/TokenBalanceInfo';
import TxDetails from './components/TxDetails';
import {
  AmountPresetButton,
  Base02TextContainer,
  DetailContainer,
  DownArrowIconContainer,
  ETHDYDXBodyContainer,
  ETHDYDXContainer,
  ETHDYDXTopContainer,
  ETHDYDXTopLeftAmountContainer,
  ETHDYDXTopLeftContainer,
  ETHDYDXTopRightContainer,
  PathwayContainer,
  StyledCircularProgress,
  StyledInput,
  SwapButtonContainer,
  SwapInfoContainer,
  TitleContainer,
  TokenContainer,
  TokenRightContainer,
  TokenTextContainer,
  TopHeaderContainer,
  WrapperContainer,
} from './styled';

import { ReactComponent as DownArrowIcon } from 'src/assets/svg/DownArrorIcon.svg';
import { ReactComponent as EthDYDXIcon } from 'src/assets/svg/EthDYDX.svg';

type BridgeProps = {
  currentExtensionNetworkId: string;
};

export default function Bridge({ currentExtensionNetworkId }: BridgeProps) {
  const { enqueueSnackbar } = useSnackbar();
  const coinGeckoPrice = useCoinGeckoPriceSWR();

  const { currentWallet } = useCurrentWallet();
  const { currentAccount } = useCurrentAccount();
  const { setCurrentTab } = useCurrentTab();

  const [currentDisplayAmount, setCurrentDisplayAmount] = useState('');
  const [currentBridgeStep, setCurrentBridgeStep] = useState(1);

  const [debouncedCurrentDisplayAmount] = useDebounce(currentDisplayAmount, 500);

  const baseDebouncedCurrentDisplayAmount = useMemo(
    () => toBaseDenomAmount(debouncedCurrentDisplayAmount || '0', DYDX_TOKEN.decimals),
    [debouncedCurrentDisplayAmount],
  );

  const displayTokenPrice = useMemo(
    () => times(debouncedCurrentDisplayAmount, coinGeckoPrice.data?.dydx?.usd || '0'),
    [debouncedCurrentDisplayAmount, coinGeckoPrice.data?.dydx?.usd],
  );

  const [isOpenBridgeTrasactionBottomsheet, setIsOpenBridgeTrasactionBottomsheet] = useState(false);

  const [transactionHash, setTransactionHash] = useState('');

  const [isSendingTx, setIsSendingTx] = useState(false);

  const [isBridgeTxTrackingFailed, setIsBridgeTxTrackingFailed] = useState(false);
  const [isBridgeTxTrackingSuccess, setIsBridgeTxTrackingSuccess] = useState(false);

  const [isApproveTxSigned, setIsApproveTxSigned] = useState(false);

  const { provider } = useProvider();

  const tokenBalance = useTokenBalanceSWR(currentWallet.name ? { address: currentAccount.accAddress, tokenContractAddress: DYDX_TOKEN_ADDRESS } : undefined);

  const ethereumBalance = useBalanceSWR({ address: currentAccount.accAddress });

  const allowanceData = useAllowanceSWR({ accAddress: currentAccount.accAddress ? currentAccount.accAddress : undefined });

  const needAllowanceCondition = useMemo(
    () =>
      allowanceData.data && !!currentWallet.name && (!gt(allowanceData.data || '0', '0') || gt(baseDebouncedCurrentDisplayAmount, allowanceData.data || '0')),
    [allowanceData.data, baseDebouncedCurrentDisplayAmount, currentWallet.name],
  );

  const allowanceTx = useMemo(() => {
    if (currentAccount.accAddress && tokenBalance.data && debouncedCurrentDisplayAmount) {
      const erc20ABI = new Interface(ERC20_ABI);

      const bridgeAmount = toBaseDenomAmount(debouncedCurrentDisplayAmount || '0', DYDX_TOKEN.decimals);

      return {
        from: currentAccount.accAddress,
        to: DYDX_TOKEN_ADDRESS,
        data: erc20ABI.encodeFunctionData('approve', [WETH_DYDX_TOKEN_ADDRESS, bridgeAmount]),
      };
    }
    return {
      from: '',
      to: '',
      data: undefined,
    };
  }, [currentAccount.accAddress, debouncedCurrentDisplayAmount, tokenBalance.data]);

  const bridgeTx = useMemo(() => {
    if (currentAccount.accAddress && currentAccount.cosmosAddress && tokenBalance && debouncedCurrentDisplayAmount) {
      const bridgeContract = new Interface(WETH_DYDX_ABI);

      const hexCosmosAddress = converter('dydx').toHex(currentAccount.cosmosAddress);

      const bridgeAmount = toBaseDenomAmount(debouncedCurrentDisplayAmount || '0', DYDX_TOKEN.decimals);

      return {
        from: currentAccount.accAddress,
        to: WETH_DYDX_TOKEN_ADDRESS,
        data: bridgeContract.encodeFunctionData('bridge', [bridgeAmount, hexCosmosAddress, '0x']),
      };
    }
    return {
      from: '',
      to: '',
      data: undefined,
    };
  }, [currentAccount.accAddress, currentAccount.cosmosAddress, debouncedCurrentDisplayAmount, tokenBalance]);

  const sendTx = useMemo(() => {
    if (needAllowanceCondition) {
      return allowanceTx;
    }
    return bridgeTx;
  }, [allowanceTx, bridgeTx, needAllowanceCondition]);

  const estimateFee = useEstimatedFeeSWR({ tx: sendTx?.from && !!currentWallet.name ? sendTx : undefined });

  const bridgeTxInfo = useTxInfoSWR(transactionHash);

  const transactionBodyText = useMemo(() => {
    if (currentBridgeStep === 1 && isBridgeTxTrackingFailed) return 'Failed to send ethDYDX to the bridge contract';

    if (currentBridgeStep === 2 && isBridgeTxTrackingFailed) return 'Failed to call the contract.';

    if (isBridgeTxTrackingSuccess) return 'Bridge Complete';

    if (currentBridgeStep === 1 && !transactionHash) {
      return `Please sign the bridge tx on your wallet.`;
    }
    if (currentBridgeStep === 1 && transactionHash) {
      return `Sending ${debouncedCurrentDisplayAmount} ethDYDX to the bridge contract.`;
    }
    if (currentBridgeStep === 2) {
      return `Receiving wethDYDX...`;
    }

    return '';
  }, [currentBridgeStep, debouncedCurrentDisplayAmount, isBridgeTxTrackingFailed, isBridgeTxTrackingSuccess, transactionHash]);

  const transactionRedirectText = useMemo(() => {
    if (isBridgeTxTrackingSuccess) return 'Staking Page';

    return '';
  }, [isBridgeTxTrackingSuccess]);

  const transactionSubText = useMemo(() => {
    if (currentBridgeStep === 1) {
      return !isBridgeTxTrackingFailed && !isBridgeTxTrackingSuccess
        ? transactionHash
          ? 'Checking the bridge status...'
          : "It's an one-way bridge. There's no coming back."
        : undefined;
    }

    if (currentBridgeStep === 2) {
      return !isBridgeTxTrackingFailed && !isBridgeTxTrackingSuccess
        ? transactionHash
          ? `You will received ${debouncedCurrentDisplayAmount} wethDYDX on your Ethereum address.`
          : undefined
        : undefined;
    }

    return '';
  }, [currentBridgeStep, debouncedCurrentDisplayAmount, isBridgeTxTrackingFailed, isBridgeTxTrackingSuccess, transactionHash]);

  const errorMessage = useMemo(() => {
    if (!currentWallet.name || !currentAccount.accAddress) {
      return 'Please connect wallet';
    }

    if (currentExtensionNetworkId !== MAINNET.hexChainId) {
      return 'Please change Network to Ethereum Mainnet';
    }

    if (!debouncedCurrentDisplayAmount || !gt(debouncedCurrentDisplayAmount, '0')) {
      return 'Please enter the quantity';
    }

    if (gt(baseDebouncedCurrentDisplayAmount, BigInt(tokenBalance.data || '0').toString(10))) {
      return 'The quantity you entered exceeds the bridgeable quantity.';
    }

    if (!tokenBalance.data || Number(BigInt(tokenBalance.data || '0').toString(10)) < 0 || Number(BigInt(tokenBalance.data || '0').toString(10)) === 0) {
      return 'There are no ethDYDX Tokens to bridge';
    }

    if (gt(estimateFee.data || '0', toDisplayDenomAmount(BigInt(ethereumBalance.data || '0').toString(10), 18))) {
      return 'Insufficient ETH balance for transaction fee';
    }

    return '';
  }, [
    currentWallet.name,
    currentAccount.accAddress,
    currentExtensionNetworkId,
    debouncedCurrentDisplayAmount,
    baseDebouncedCurrentDisplayAmount,
    tokenBalance.data,
    estimateFee.data,
    ethereumBalance.data,
  ]);

  useEffect(() => {
    pageView('Bridge', 'Bridge');
  }, []);

  useEffect(() => {
    if (isOpenBridgeTrasactionBottomsheet) {
      if (bridgeTxInfo.data?.status === '0x0' || bridgeTxInfo.error) {
        setIsBridgeTxTrackingFailed(true);
      }
      if (bridgeTxInfo.data?.status === '0x1') {
        setCurrentBridgeStep(3);
        setIsBridgeTxTrackingSuccess(true);
      }
    }
  }, [bridgeTxInfo.data?.status, bridgeTxInfo.error, isOpenBridgeTrasactionBottomsheet]);

  useEffect(() => {
    if (!isOpenBridgeTrasactionBottomsheet && (isBridgeTxTrackingFailed || isBridgeTxTrackingSuccess)) {
      setIsBridgeTxTrackingSuccess(false);
      setIsBridgeTxTrackingFailed(false);
    }
  }, [isBridgeTxTrackingFailed, isBridgeTxTrackingSuccess, isOpenBridgeTrasactionBottomsheet]);

  useEffect(() => {
    if (isApproveTxSigned && !needAllowanceCondition) {
      setIsApproveTxSigned(false);
      setIsSendingTx(false);
    }
  }, [isApproveTxSigned, isBridgeTxTrackingFailed, isBridgeTxTrackingSuccess, isOpenBridgeTrasactionBottomsheet, needAllowanceCondition]);

  return (
    <>
      <TopHeaderContainer>
        <TitleContainer>
          <Typography variant="h1">DYDX BRIDGE</Typography>
        </TitleContainer>
        <NetworkBadge />
      </TopHeaderContainer>

      <PathwayContainer>
        <ETHDYDXContainer>
          <ETHDYDXTopContainer>
            <ETHDYDXTopLeftContainer>
              <Typography variant="h7">Available</Typography>
              &nbsp;
              <Typography variant="h4">:</Typography>
              <ETHDYDXTopLeftAmountContainer>
                &nbsp;
                <NumberTypo typoOfIntegers="h6" typoOfDecimals="h8">
                  {String(parseFloat(fix(toDisplayDenomAmount(tokenBalance.data || '0', DYDX_TOKEN.decimals), 6)))}
                </NumberTypo>
              </ETHDYDXTopLeftAmountContainer>
            </ETHDYDXTopLeftContainer>
            <ETHDYDXTopRightContainer>
              <AmountPresetButton
                onClick={() => {
                  setCurrentDisplayAmount(trimZeros(times(toDisplayDenomAmount(tokenBalance.data || '0', DYDX_TOKEN.decimals), '0.25')));
                }}
              >
                <Typography variant="h10">1/4</Typography>
              </AmountPresetButton>

              <AmountPresetButton
                onClick={() => {
                  setCurrentDisplayAmount(trimZeros(times(toDisplayDenomAmount(tokenBalance.data || '0', DYDX_TOKEN.decimals), '0.5')));
                }}
              >
                <Typography variant="h10">HALF</Typography>
              </AmountPresetButton>

              <AmountPresetButton
                onClick={() => {
                  setCurrentDisplayAmount(trimZeros(toDisplayDenomAmount(tokenBalance.data || '0', DYDX_TOKEN.decimals)));
                }}
              >
                <Typography variant="h10">MAX</Typography>
              </AmountPresetButton>
            </ETHDYDXTopRightContainer>
          </ETHDYDXTopContainer>
          <ETHDYDXBodyContainer>
            <TokenContainer>
              <EthDYDXIcon />
              <TokenTextContainer>
                <Typography variant="h4">ethDYDX</Typography>
              </TokenTextContainer>
            </TokenContainer>
            <TokenRightContainer>
              <StyledInput
                onChange={(e) => {
                  if (!isDecimal(e.currentTarget.value, DYDX_TOKEN.decimals) && e.currentTarget.value) {
                    return;
                  }

                  setCurrentDisplayAmount(e.currentTarget.value);
                }}
                value={currentDisplayAmount}
                placeholder="0"
              />
              <Base02TextContainer>
                <Typography variant="h8">$</Typography>
                &nbsp;
                {gt(displayTokenPrice || '0', '0') ? (
                  <NumberTypo typoOfIntegers="h8" typoOfDecimals="h10">
                    {displayTokenPrice}
                  </NumberTypo>
                ) : (
                  <NumberTypo typoOfIntegers="h8" typoOfDecimals="h10">
                    0.000
                  </NumberTypo>
                )}
              </Base02TextContainer>
            </TokenRightContainer>
          </ETHDYDXBodyContainer>
        </ETHDYDXContainer>
        <DownArrowIconContainer>
          <DownArrowIcon />
        </DownArrowIconContainer>
      </PathwayContainer>

      <SwapInfoContainer>
        <TokenBalanceInfo currentInputAmount={debouncedCurrentDisplayAmount} />
      </SwapInfoContainer>

      <DetailContainer>
        <TxDetails estimatedGasFee={estimateFee.data || ''} />
      </DetailContainer>

      <SwapButtonContainer>
        <Tooltip varient="error" title={errorMessage} placement="top" arrow>
          <div>
            <GradientButton
              disabled={!!errorMessage || !provider || isSendingTx}
              onClick={async () => {
                if (!provider) return;
                try {
                  setIsSendingTx(true);

                  if (needAllowanceCondition) {
                    const allowanceTxHash = await provider.send('eth_sendTransaction', [allowanceTx]);

                    enqueueSnackbar('Transaction broadcasting success', {
                      variant: 'success',
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      txHash: allowanceTxHash as string,
                    });

                    await allowanceData.mutate();

                    setIsApproveTxSigned(true);
                  } else {
                    setIsOpenBridgeTrasactionBottomsheet(true);

                    const bridgeTxHash = await provider.send('eth_sendTransaction', [bridgeTx]);
                    bridgingClickEvent('Bridge Tab', undefined, debouncedCurrentDisplayAmount ? Number(debouncedCurrentDisplayAmount) : undefined, {
                      bridged_amount: debouncedCurrentDisplayAmount || '0',
                    });

                    setTransactionHash(bridgeTxHash as string);

                    enqueueSnackbar('Transaction broadcasting success', {
                      variant: 'success',
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      txHash: bridgeTxHash as string,
                    });

                    setCurrentBridgeStep(2);

                    setIsSendingTx(false);
                  }
                } catch (e) {
                  setIsSendingTx(false);

                  if (!needAllowanceCondition) {
                    setIsBridgeTxTrackingFailed(true);
                  }

                  const etherError = e as EthersError;

                  const etherErrorInfo = etherError.info?.error as RPCError;

                  enqueueSnackbar('Transaction Failed', {
                    variant: 'error',
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    errorMsg: RPC_ERROR_MESSAGE[etherErrorInfo.code] || '',

                    autoHideDuration: 3000,
                  });
                }
              }}
            >
              {isSendingTx ? (
                <StyledCircularProgress size={20} />
              ) : (
                <Typography variant="h4">{gt(currentDisplayAmount || '0', '0') && needAllowanceCondition ? 'Approve ethDYDX SPENDING' : 'BRIDGE'}</Typography>
              )}
            </GradientButton>
          </div>
        </Tooltip>
      </SwapButtonContainer>
      <WrapperContainer data-is-open={isOpenBridgeTrasactionBottomsheet}>
        <TransactionBottomSheet
          open={isOpenBridgeTrasactionBottomsheet}
          variant="persistent"
          title="Bridge Status"
          bodyText={transactionBodyText}
          subText={transactionSubText}
          currentStep={currentBridgeStep}
          redirectButtonText={transactionRedirectText}
          isFailed={isBridgeTxTrackingFailed}
          isSuccess={isBridgeTxTrackingSuccess}
          isBridge
          onClickRedirect={() => {
            setCurrentTab({ tabIndex: 1 });
          }}
          onClose={() => {
            setIsOpenBridgeTrasactionBottomsheet(false);
            setIsBridgeTxTrackingFailed(false);
            setIsBridgeTxTrackingSuccess(false);
            setTransactionHash('');
            setCurrentBridgeStep(1);
          }}
        />
      </WrapperContainer>
    </>
  );
}
